import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Heart, MessageSquare, Calendar, Users, 
  ChevronRight, Clock, Eye, ThumbsUp, 
  PenSquare, Search, Filter, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 서브 카테고리 정의
const categories = [
  { 
    id: "success", 
    label: "성공스토리", 
    icon: Heart, 
    color: "text-pink-500",
    bgColor: "bg-pink-50",
    description: "커플 매칭 성공 사례를 공유합니다"
  },
  { 
    id: "date-review", 
    label: "데이트 후기", 
    icon: Star, 
    color: "text-gold-500",
    bgColor: "bg-gold-50",
    description: "데이트 후기와 추천 장소를 나눠요"
  },
  { 
    id: "event", 
    label: "이벤트", 
    icon: Calendar, 
    color: "text-burgundy-500",
    bgColor: "bg-burgundy-50",
    description: "커플 이벤트 및 모임 안내"
  },
  { 
    id: "chat", 
    label: "커플 채팅방", 
    icon: MessageSquare, 
    color: "text-blue-500",
    bgColor: "bg-blue-50",
    description: "커플들의 실시간 소통 공간",
    isLink: true,
    href: "/couple-chat"
  },
];

// 샘플 게시글 데이터
const samplePosts = {
  "success": [
    { id: 1, title: "New Coupling에서 운명의 상대를 만났어요!", author: "행복한커플", date: "2024-03-25", views: 324, likes: 89, comments: 23 },
    { id: 2, title: "50대에 새로운 사랑을 시작합니다", author: "봄날의햇살", date: "2024-03-24", views: 256, likes: 67, comments: 15 },
    { id: 3, title: "3개월 만에 결혼까지! 감사합니다", author: "사랑스러운", date: "2024-03-23", views: 512, likes: 134, comments: 42 },
    { id: 4, title: "줌미팅에서 만난 우리, 이제 약혼했어요", author: "달콤한인연", date: "2024-03-22", views: 189, likes: 45, comments: 18 },
  ],
  "date-review": [
    { id: 5, title: "첫 데이트 장소 추천 - 한강공원", author: "로맨티스트", date: "2024-03-25", views: 156, likes: 34, comments: 12 },
    { id: 6, title: "분위기 좋은 레스토랑 후기", author: "미식가커플", date: "2024-03-24", views: 203, likes: 56, comments: 8 },
    { id: 7, title: "경주 여행 데이트 코스 공유해요", author: "여행러버", date: "2024-03-23", views: 178, likes: 41, comments: 15 },
  ],
  "event": [
    { id: 8, title: "[공지] 4월 커플 소풍 이벤트 안내", author: "New Coupling 운영팀", date: "2024-03-25", views: 423, likes: 78, comments: 32, isPinned: true },
    { id: 9, title: "[모집] 봄맞이 커플 와인파티", author: "이벤트매니저", date: "2024-03-24", views: 287, likes: 52, comments: 19 },
    { id: 10, title: "[후기] 지난 커플 볼링대회 후기", author: "즐거운커플", date: "2024-03-22", views: 145, likes: 38, comments: 11 },
  ],
  "chat": [
    { id: 11, title: "40대 커플 채팅방 입장하기", author: "시스템", date: "2024-03-25", views: 567, likes: 0, comments: 0, isChat: true },
    { id: 12, title: "50대 커플 채팅방 입장하기", author: "시스템", date: "2024-03-25", views: 432, likes: 0, comments: 0, isChat: true },
    { id: 13, title: "60대 커플 채팅방 입장하기", author: "시스템", date: "2024-03-25", views: 321, likes: 0, comments: 0, isChat: true },
    { id: 14, title: "전체 커플 오픈채팅방", author: "시스템", date: "2024-03-25", views: 789, likes: 0, comments: 0, isChat: true },
  ],
};

export default function CoupleBoardPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "success";
  
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [searchQuery, setSearchQuery] = useState("");
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lizCouples, setLizCouples] = useState([]);

  useEffect(() => {
    setSelectedCategory(categoryParam);
  }, [categoryParam]);

  useEffect(() => {
    fetch(`${API_URL}/api/liz-couples`).then(r => r.ok ? r.json() : { couples: [] }).then(d => setLizCouples((d.couples || []).filter(c => c.is_visible !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      try {
        const catParam = selectedCategory !== "all" ? `?category=${selectedCategory}` : "";
        const res = await fetch(`${API_URL}/api/couple-posts${catParam}`);
        if (res.ok) setPosts(await res.json());
      } catch { /* ignore */ }
      setIsLoading(false);
    };
    if (selectedCategory !== "chat") fetchPosts();
  }, [selectedCategory]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchParams({ category: categoryId });
  };

  const currentCategory = categories.find(c => c.id === selectedCategory) || categories[0];

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return post.title.toLowerCase().includes(query) || 
           post.author.toLowerCase().includes(query);
  });

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Hero Section */}
      <section className="relative h-[380px] md:h-[440px] overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/newcoupling/images/55bb88ed-4943-4bf9-8f90-b93925198f0d.png`}
            alt="couple wedding rings"
            className="w-full h-full object-cover"
          />
          {/* 부드러운 비네팅 + 와인색 톤 */}
          <div className="absolute inset-0 bg-gradient-to-b from-burgundy-950/55 via-burgundy-900/40 to-burgundy-950/75" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_0%,rgba(0,0,0,0.35)_100%)]" />
        </div>

        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="flex flex-col items-center"
          >
            {/* 우아한 상단 라인 + 키워드 */}
            <div className="flex items-center gap-3 mb-6">
              <span className="block h-px w-8 bg-pink-200/70" />
              <Heart className="w-3.5 h-3.5 text-pink-200 fill-pink-200" />
              <span className="text-pink-100/90 text-[11px] font-medium tracking-[0.32em] uppercase">
                Liz Couples
              </span>
              <Heart className="w-3.5 h-3.5 text-pink-200 fill-pink-200" />
              <span className="block h-px w-8 bg-pink-200/70" />
            </div>

            <h1
              className="text-5xl md:text-6xl font-bold text-white tracking-tight"
              style={{ textShadow: '0 4px 28px rgba(20,8,16,0.55)' }}
            >
              리즈 커플즈
            </h1>

            <p className="mt-5 text-white/85 max-w-xl mx-auto text-[15px] leading-relaxed">
              성공 스토리를 공유하고, 데이트 후기를 나누며<br className="hidden md:block" />
              다른 커플들과 따뜻한 인연을 이어가세요
            </p>
          </motion.div>

          {/* Category Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="flex flex-wrap justify-center gap-2.5 mt-8"
          >
            {categories.filter(c => !c.isLink).map((cat) => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.id)}
                  className={`flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-medium backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 ${
                    selectedCategory === cat.id
                      ? "bg-white text-burgundy-600 shadow-xl shadow-black/20"
                      : "bg-white/10 text-white border border-white/25 hover:bg-white/20"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat.label}
                </button>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Left Sidebar - Categories */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden sticky top-24">
              <div className="p-4 bg-gradient-to-r from-burgundy-500 to-burgundy-600">
                <h2 className="text-white font-bold flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  리즈 커플즈
                </h2>
              </div>
              <nav className="p-2">
                {categories.map((category) => {
                  const Icon = category.icon;
                  const isActive = selectedCategory === category.id;
                  return (
                    <button
                      key={category.id}
                      onClick={() => {
                        if (category.isLink && category.href) {
                          navigate(category.href);
                        } else {
                          handleCategoryChange(category.id);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        isActive && !category.isLink
                          ? "bg-burgundy-50 text-burgundy-600" 
                          : "text-stone-600 hover:bg-stone-50 hover:text-burgundy-500"
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isActive && !category.isLink ? category.bgColor : "bg-stone-100"
                      }`}>
                        <Icon className={`w-4 h-4 ${isActive && !category.isLink ? category.color : "text-stone-400"}`} />
                      </div>
                      <span className={`font-medium ${isActive && !category.isLink ? "text-burgundy-600" : ""}`}>
                        {category.label}
                      </span>
                      {category.isLink ? (
                        <ChevronRight className="w-4 h-4 ml-auto text-stone-400" />
                      ) : isActive && (
                        <ChevronRight className="w-4 h-4 ml-auto text-burgundy-400" />
                      )}
                    </button>
                  );
                })}
              </nav>
              
              {/* 리즈커플들 - 위 카테고리와 동일한 구조 */}
              <div className="border-t border-stone-100 px-2 pt-2 pb-2">
                <button
                  onClick={() => navigate("/liz-couples")}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-stone-600 hover:bg-stone-50 hover:text-burgundy-500 transition-all duration-200"
                  data-testid="category-liz-couples"
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-stone-100">
                    <Heart className="w-4 h-4 text-stone-400" />
                  </div>
                  <span className="font-medium">리즈커플들</span>
                  <ChevronRight className="w-4 h-4 ml-auto text-stone-400" />
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1">
            {/* Category Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${currentCategory.bgColor}`}>
                    <currentCategory.icon className={`w-6 h-6 ${currentCategory.color}`} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-stone-800">{currentCategory.label}</h2>
                    <p className="text-stone-500 text-sm">{currentCategory.description}</p>
                  </div>
                </div>
                
                {/* Search */}
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                      type="text"
                      placeholder="검색..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500/20 focus:border-burgundy-500 w-full sm:w-64"
                    />
                  </div>
                  {(selectedCategory === "success" || selectedCategory === "date-review") && (
                    <Button
                      data-testid="write-post-btn"
                      className="bg-burgundy-500 hover:bg-burgundy-600 text-white flex items-center gap-2 whitespace-nowrap"
                      onClick={() => {
                        if (!isAuthenticated) {
                          alert("로그인 후 글쓰기가 가능합니다.");
                          return;
                        }
                        navigate(`/couple-board/write?category=${selectedCategory}`);
                      }}
                    >
                      <PenSquare className="w-4 h-4" />
                      글쓰기
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Posts List */}
            <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
              {filteredPosts.length === 0 ? (
                <div className="p-12 text-center">
                  <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-stone-500">게시글이 없습니다.</p>
                </div>
              ) : (
                <div className="divide-y divide-stone-100">
                  {filteredPosts.map((post, index) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`p-4 hover:bg-stone-50 cursor-pointer transition-colors ${
                        post.isPinned ? "bg-burgundy-50/50" : ""
                      }`}
                      onClick={() => navigate(`/couple-board/${post.id}`)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Post Icon */}
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          post.isPinned ? "bg-burgundy-100" : 
                          post.isChat ? "bg-blue-100" : "bg-stone-100"
                        }`}>
                          {post.isPinned ? (
                            <Calendar className="w-5 h-5 text-burgundy-500" />
                          ) : post.isChat ? (
                            <MessageSquare className="w-5 h-5 text-blue-500" />
                          ) : (
                            <PenSquare className="w-5 h-5 text-stone-400" />
                          )}
                        </div>
                        
                        {/* Post Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {post.isPinned && (
                              <span className="px-2 py-0.5 bg-burgundy-500 text-white text-xs rounded-full">
                                공지
                              </span>
                            )}
                            {post.isChat && (
                              <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                                채팅
                              </span>
                            )}
                            <h3 className="font-medium text-stone-800 truncate">
                              {post.title}
                            </h3>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-stone-500">
                            <span>{post.author}</span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {(() => {
                                const d = post.created_at || post.date;
                                if (!d) return "";
                                const diff = Math.floor((new Date() - new Date(d)) / 60000);
                                if (diff < 1) return "방금 전";
                                if (diff < 60) return `${diff}분 전`;
                                if (diff < 1440) return `${Math.floor(diff / 60)}시간 전`;
                                if (diff < 10080) return `${Math.floor(diff / 1440)}일 전`;
                                return new Date(d).toLocaleDateString("ko-KR");
                              })()}
                            </span>
                          </div>
                        </div>
                        
                        {/* Post Stats */}
                        <div className="flex items-center gap-4 text-xs text-stone-400 flex-shrink-0">
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views}
                          </span>
                          {!post.isChat && (
                            <>
                              <span className="flex items-center gap-1">
                                <ThumbsUp className="w-3 h-3" />
                                {post.likes}
                              </span>
                              <span className="flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                {post.comments}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Pagination (Placeholder) */}
            <div className="mt-6 flex justify-center">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled>이전</Button>
                <Button variant="outline" size="sm" className="bg-burgundy-500 text-white border-burgundy-500">1</Button>
                <Button variant="outline" size="sm">2</Button>
                <Button variant="outline" size="sm">3</Button>
                <Button variant="outline" size="sm">다음</Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
